import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAnalysisStore, AssessmentHistoryItem, ProgressEvent } from '../../store/analysisStore';
import {
  Box,
  Typography,
  Button,
  Container,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AssessmentResult from './AssessmentResult';

export default function AssessmentDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { fetchRecordById, assessmentHistory, resetAssessment, submitFeedback, currentUser } = useAnalysisStore();
  const isAdmin = currentUser?.role === 'admin';

  const handleNewAssessment = () => {
    resetAssessment();
    navigate('/');
  };
  const [record, setRecord] = useState<AssessmentHistoryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const openFeedbackDialog = () => {
    setFeedbackText(record?.feedback ?? '');
    setFeedbackError(null);
    setFeedbackOpen(true);
  };

  const closeFeedbackDialog = () => {
    if (feedbackSubmitting) return;
    setFeedbackOpen(false);
  };

  const handleSubmitFeedback = async () => {
    if (!record) return;
    const trimmed = feedbackText.trim();
    if (!trimmed) {
      setFeedbackError('Please enter your feedback before submitting.');
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    const updated = await submitFeedback(record.id, trimmed);
    setFeedbackSubmitting(false);
    if (!updated) {
      setFeedbackError('Failed to submit feedback. Please try again.');
      return;
    }
    setRecord(updated);
    setFeedbackOpen(false);
  };

  useEffect(() => {
    async function loadRecord() {
      if (!id) return;

      // Admins always fetch from the server so the (admin-only) progress_events
      // log is present — the local history cache doesn't carry it.
      if (!isAdmin) {
        const local = assessmentHistory.find(item => item.id === id);
        if (local) {
          setRecord(local);
          setLoading(false);
          return;
        }
      }

      const fetched = await fetchRecordById(id);
      setRecord(fetched);
      setLoading(false);
    }

    loadRecord();
  }, [id, fetchRecordById, assessmentHistory, isAdmin]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!record) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Assessment record not found.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
          >
            Trust Index
          </Link>
          <Typography color="text.primary">Assessment Details</Typography>
        </Breadcrumbs>
        
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Analysis Details
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={record.provider.toUpperCase()}
                size="small"
                color={record.provider === 'gemini' ? 'secondary' : 'primary'}
              />
              <Typography variant="body2" color="text.secondary">
                Assessed on {new Date(record.timestamp).toLocaleString()}
              </Typography>
            </Stack>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate(`/dashboard/${record.source_id}`)}
              fullWidth={isMobile}
            >
              View Source Dashboard
            </Button>
            <Button
              variant="contained"
              startIcon={<RestartAltIcon />}
              onClick={handleNewAssessment}
              fullWidth={isMobile}
              sx={{
                bgcolor: '#1a73e8',
                textTransform: 'none',
                '&:hover': { bgcolor: '#1967d2' },
              }}
            >
              New Assessment
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              fullWidth={isMobile}
            >
              Back
            </Button>
          </Stack>
        </Box>
      </Box>

      <Divider sx={{ mb: isAdmin ? 2 : 4 }} />

      {isAdmin && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Assessment" />
          <Tab
            label={`Events${record.progress_events?.length ? ` (${record.progress_events.length})` : ''}`}
          />
        </Tabs>
      )}

      {(!isAdmin || tab === 0) && (
        <>
          <AssessmentResult
            result={record.result}
            extraActions={
              <Button
                variant="contained"
                startIcon={<FeedbackIcon />}
                onClick={openFeedbackDialog}
                sx={{
                  bgcolor: '#1a73e8',
                  textTransform: 'none',
                  borderRadius: '20px',
                  '&:hover': { bgcolor: '#1967d2' },
                }}
              >
                {record.feedback ? 'Edit your feedback' : 'Give us your feedback'}
              </Button>
            }
          />

          {record.feedback && (
            <Paper
              variant="outlined"
              sx={{ mt: 4, p: 3, borderRadius: 2, borderColor: '#aecbfa', bgcolor: '#f8fbff' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <FeedbackIcon sx={{ color: '#1a73e8', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Your feedback
                </Typography>
                {record.feedback_timestamp && (
                  <Typography variant="caption" color="text.secondary">
                    · {new Date(record.feedback_timestamp).toLocaleString()}
                  </Typography>
                )}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.feedback}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {isAdmin && tab === 1 && <AssessmentEventsPanel events={record.progress_events} />}

      <Dialog
        open={feedbackOpen}
        onClose={closeFeedbackDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Give us your feedback</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please help us improve our product. Share your thoughts on this assessment. Your feedback is saved with this article in your assessment history.
          </Typography>
          {feedbackError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {feedbackError}
            </Alert>
          )}
          <TextField
            autoFocus
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            placeholder="What did you think of this assessment?"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            inputProps={{ maxLength: 5000 }}
            disabled={feedbackSubmitting}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
            {feedbackText.length} / 5000
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFeedbackDialog} disabled={feedbackSubmitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitFeedback}
            disabled={feedbackSubmitting || !feedbackText.trim()}
            variant="contained"
            sx={{
              bgcolor: '#1a73e8',
              textTransform: 'none',
              borderRadius: '20px',
              '&:hover': { bgcolor: '#1967d2' },
            }}
          >
            {feedbackSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

/** Admin-only panel: the timed log of streamed progress events for an assessment. */
function AssessmentEventsPanel({ events }: { events?: ProgressEvent[] }) {
  if (!events || events.length === 0) {
    return (
      <Alert severity="info">
        No streamed event log was captured for this assessment. Event logging was added recently,
        so assessments run before then won't have one.
      </Alert>
    );
  }

  const colorFor = (kind: string) =>
    kind === 'error' ? '#d93025'
      : kind === 'result' ? '#188038'
      : kind === 'outcome' ? '#1967d2'
      : '#5f6368';

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 2, bgcolor: '#f8fbff', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Streamed progress events
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Captured live during this assessment · {events.length} step{events.length === 1 ? '' : 's'}
        </Typography>
      </Box>
      <Stack divider={<Divider />}>
        {events.map((e, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, px: 3, py: 1.25, alignItems: 'baseline' }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', color: '#5f6368', minWidth: 56, textAlign: 'right' }}
            >
              +{e.t.toFixed(1)}s
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colorFor(e.kind), fontWeight: e.kind === 'progress' ? 400 : 600, wordBreak: 'break-word' }}
            >
              {e.message}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
